/**
 * Friendly label for a content-bundle override URL.
 *
 * GCS build paths look like:
 *   .../20260923-152504-a48efdb-709f/tt-games.js
 * which we surface as "23rd Sept". When no YYYYMMDD stamp is present, fall back
 * to the final path segment so the cell is still useful.
 */
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
] as const;

function ordinalDay(day: number): string {
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

/** Extract a YYYYMMDD stamp from a content-bundle URL path, if present. */
export function parseContentBundleBuildDate(url: string): Date | null {
  const match = url.match(/(?:^|\/)(\d{8})-\d{6}-[^/?#]+/);
  if (!match) return null;
  const ymd = match[1];
  const year = Number(ymd.slice(0, 4));
  const month = Number(ymd.slice(4, 6));
  const day = Number(ymd.slice(6, 8));
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

export function formatContentBundleLabel(url: string): string {
  const date = parseContentBundleBuildDate(url);
  if (date) {
    return `${ordinalDay(date.getUTCDate())} ${MONTHS[date.getUTCMonth()]}`;
  }
  try {
    const path = new URL(url).pathname;
    const segment = path.split("/").filter(Boolean).pop();
    if (segment) return segment;
  } catch {
    // not a URL
  }
  return url;
}
